/**
 * OrdersStackedChart — 30-day stacked area chart of active orders per
 * workflow stage.
 *
 * Data source: `orders_snapshot/{YYYY-MM-DD-HH}` — populated by the
 * snapshotOrdersByStage Cloud Function twice daily (12:00 + 20:00 Berlin).
 * Fields per doc: `date`, `hour`, `counts{new,grafiker,entwurf_raus,
 * produktion,druck,kerzen}`, `total`.
 *
 * Chart starts empty on deploy — fills up over 30 days as CF writes each
 * snapshot. No backfill (see conversation 2026-08-11: too imprecise).
 */
import React, { useEffect, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";

const DAYS = 30;

// Stage config: label + color, in the order they stack (bottom → top).
// Workflow order = intuitive: "new" at bottom (starts here), "kerzen" at
// top (specialty). Colors picked for good contrast + Printsonalities feel.
const STAGES = [
  { key: "new",          label: "Neu",          color: "#a0aec0" },  // gray
  { key: "grafiker",     label: "Grafiker",     color: "#805ad5" },  // purple
  { key: "entwurf_raus", label: "Entwurf raus", color: "#dd6b20" },  // orange
  { key: "produktion",   label: "Produktion",   color: "#3182ce" },  // blue
  { key: "druck",        label: "Druck",        color: "#38a169" },  // green
  { key: "kerzen",       label: "Kerzen",       color: "#d69e2e" },  // gold
];

// ── Helpers ──────────────────────────────────────────────────────────────

function berlinTodayBucket() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Berlin", year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(new Date());
  const y = parts.find(p => p.type === "year").value;
  const m = parts.find(p => p.type === "month").value;
  const d = parts.find(p => p.type === "day").value;
  return `${y}-${m}-${d}`;
}

// Return list of YYYY-MM-DD strings for the last DAYS days ending today (Berlin)
function buildDateList() {
  const today = berlinTodayBucket();
  const [y, m, d] = today.split("-").map(Number);
  const base = new Date(Date.UTC(y, m - 1, d));
  const list = [];
  for (let i = DAYS - 1; i >= 0; i--) {
    const dt = new Date(base.getTime() - i * 86400000);
    const yy = dt.getUTCFullYear();
    const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(dt.getUTCDate()).padStart(2, "0");
    list.push(`${yy}-${mm}-${dd}`);
  }
  return list;
}

function fmtDayLabel(dayStr) {
  const [, mm, dd] = dayStr.split("-");
  return `${dd}.${mm}.`;
}

// ── Component ────────────────────────────────────────────────────────────

export default function OrdersStackedChart() {
  const [snapshots, setSnapshots] = useState({});  // {docId: {counts, total, hour, date}}
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "orders_snapshot"),
      (snap) => {
        const map = {};
        snap.forEach(d => { map[d.id] = d.data(); });
        setSnapshots(map);
        setLoading(false);
      },
      (err) => {
        console.error("OrdersStackedChart subscribe failed:", err);
        setLoading(false);
      }
    );
    return unsub;
  }, []);

  // ── Build data-points for the chart ────────────────────────────────
  // Each day gets up to 2 datapoints (12h + 20h).
  // If a day has neither → skipped (chart interpolates through gap).
  const days = buildDateList();
  const dataPoints = [];  // [{ label, date, hour, counts, total }]
  for (const day of days) {
    for (const hour of [12, 20]) {
      const docId = `${day}-${String(hour).padStart(2, "0")}`;
      const snap = snapshots[docId];
      if (snap) {
        dataPoints.push({
          docId, date: day, hour,
          counts: snap.counts || {},
          total: snap.total || 0,
        });
      }
    }
  }

  const maxTotal = Math.max(20, ...dataPoints.map(p => p.total));

  // ── SVG geometry (responsive via viewBox) ──────────────────────────
  const W = 780, H = 220;
  const padL = 40, padR = 12, padT = 12, padB = 34;
  const chartW = W - padL - padR;
  const chartH = H - padT - padB;

  // X mapping: distribute datapoints uniformly across chart width.
  // (2 points per day means points are spaced within the day too — the
  // 12h point at fraction 0.4, the 20h point at fraction 0.7 of that day.)
  // Simpler for now: uniform spacing regardless of hour, chronological order.
  const xStep = dataPoints.length > 1 ? chartW / (dataPoints.length - 1) : 0;
  const xAt = i => padL + i * xStep;
  const yAt = v => padT + chartH - (v / maxTotal) * chartH;

  // Build one polygon path per stage — stacked from bottom.
  // For each datapoint we compute cumulative y-tops per stage.
  const stagePaths = STAGES.map((stage, sIdx) => {
    const topPoints = [];
    const bottomPoints = [];
    for (let i = 0; i < dataPoints.length; i++) {
      const p = dataPoints[i];
      let cumBelow = 0;
      for (let j = 0; j < sIdx; j++) cumBelow += p.counts[STAGES[j].key] || 0;
      const cumIncl = cumBelow + (p.counts[stage.key] || 0);
      topPoints.push(`${xAt(i)},${yAt(cumIncl)}`);
      bottomPoints.push(`${xAt(i)},${yAt(cumBelow)}`);
    }
    const path = "M " + topPoints.join(" L ") +
                 " L " + bottomPoints.reverse().join(" L ") + " Z";
    return { stage, path };
  });

  // Y-axis ticks (0, mid, max)
  const yTicks = [0, Math.round(maxTotal / 2), maxTotal];

  // X-axis: show ~every 5th day label (avoid crowding)
  const xLabels = days.map((day, i) => {
    if (i % 5 !== 0 && i !== days.length - 1) return null;
    // find the datapoint whose date matches — use its position
    const dpIdx = dataPoints.findIndex(p => p.date === day);
    if (dpIdx < 0) return null;
    return {
      x: xAt(dpIdx),
      label: fmtDayLabel(day),
      isToday: day === berlinTodayBucket(),
    };
  }).filter(Boolean);

  return (
    <div style={{
      background: "#fff",
      borderRadius: "12px",
      boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
      padding: "1.5em",
      marginBottom: "1.5em",
    }}>
      <div style={{
        fontSize: "0.75rem",
        color: "#4a5568",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        marginBottom: "0.6em",
      }}>
        Aktive Bestellungen pro Stage ({DAYS} Tage)
      </div>

      {loading ? (
        <p style={{ fontSize: "0.85rem", color: "#718096" }}>Lade Daten…</p>
      ) : dataPoints.length === 0 ? (
        <div style={{
          fontSize: "0.85rem",
          color: "#718096",
          padding: "2em 0",
          textAlign: "center",
          lineHeight: 1.6,
        }}>
          Noch keine Snapshots vorhanden.<br />
          <span style={{ fontSize: "0.78rem" }}>
            Der Snapshot-Job schreibt täglich um 12:00 und 20:00 Berlin-Zeit
            — der erste Datenpunkt erscheint nach dem nächsten Termin.
          </span>
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <svg viewBox={`0 0 ${W} ${H}`}
               preserveAspectRatio="xMidYMid meet"
               style={{ width: "100%", minWidth: 640, height: "auto",
                        display: "block" }}>
            {/* Grid lines */}
            {yTicks.map(v => (
              <line key={`grid-${v}`}
                    x1={padL} x2={W - padR}
                    y1={yAt(v)} y2={yAt(v)}
                    stroke="#e2e8f0"
                    strokeDasharray={v === 0 ? "" : "2,3"}
                    strokeWidth="1" />
            ))}
            {/* Y-axis labels */}
            {yTicks.map(v => (
              <text key={`ylab-${v}`}
                    x={padL - 6} y={yAt(v) + 4}
                    textAnchor="end"
                    fontSize="10"
                    fill="#a0aec0"
                    fontFamily="system-ui, sans-serif">
                {v}
              </text>
            ))}
            {/* Stacked area fills */}
            {stagePaths.map(({ stage, path }) => (
              <path key={stage.key}
                    d={path}
                    fill={stage.color}
                    fillOpacity="0.72"
                    stroke={stage.color}
                    strokeWidth="1" />
            ))}
            {/* Hover invisible bars for tooltip per datapoint */}
            {dataPoints.map((p, i) => {
              const x = xAt(i);
              const halfStep = xStep / 2 || 20;
              return (
                <rect key={p.docId}
                      x={x - halfStep} y={padT}
                      width={xStep || 40} height={chartH}
                      fill="transparent">
                  <title>
                    {`${fmtDayLabel(p.date)} ${String(p.hour).padStart(2, "0")}:00\n`
                      + STAGES.map(s =>
                        `${s.label}: ${p.counts[s.key] || 0}`).join("\n")
                      + `\n────\nTotal: ${p.total}`}
                  </title>
                </rect>
              );
            })}
            {/* X-axis date labels */}
            {xLabels.map(({ x, label, isToday }, i) => (
              <text key={`xlab-${i}`}
                    x={x} y={H - 14}
                    textAnchor="middle"
                    fontSize="10"
                    fill={isToday ? "#553c9a" : "#a0aec0"}
                    fontWeight={isToday ? 700 : 400}
                    fontFamily="system-ui, sans-serif">
                {label}
              </text>
            ))}
          </svg>
        </div>
      )}

      {/* Legend */}
      {!loading && (
        <div style={{
          marginTop: "1em",
          fontSize: "0.72rem",
          color: "#4a5568",
          display: "flex",
          alignItems: "center",
          gap: "1em",
          flexWrap: "wrap",
        }}>
          {STAGES.map(s => (
            <div key={s.key}
                 style={{ display: "flex", alignItems: "center", gap: "0.35em" }}>
              <span style={{
                width: 12, height: 12,
                background: s.color,
                borderRadius: "2px",
                display: "inline-block",
              }} />
              <span>{s.label}</span>
            </div>
          ))}
          <span style={{ marginLeft: "auto", color: "#a0aec0" }}>
            2× täglich (12:00 + 20:00 Berlin) · Hover für Details
          </span>
        </div>
      )}
    </div>
  );
}
